import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FlowEngineGlobalGate from "../specialist-flow-engine/FlowEngineGlobalGate";
import FlowStateSemaphore from "../specialist-flow-engine/components/FlowStateSemaphore";
import FlowRecordHistoryModal from "../specialist-flow-engine/components/FlowRecordHistoryModal";
import FlowCaseFilterBar from "../specialist-flow-engine/FlowCaseFilterBar";
import FlowCaseMassToolbar from "../specialist-flow-engine/FlowCaseMassToolbar";
import FlowMassReassignPanel from "../specialist-flow-engine/FlowMassReassignPanel";
import RecordExcelUpdateRowButton from "./components/RecordExcelUpdateRowButton";
import TrayVisionName from "./TrayVisionName";
import {
  clearFlowCmiDetail,
  fetchFlowCmiCasesRequest,
  startLoadingFlowCmiCase,
  startLoadingFlowCmiDashboard,
  startSearchingFlowCmiCases
} from "../../app/actions/flowCmiActions";
import { startLoadingFlowEngineCatalogs } from "../../app/actions/flowEngineActions";
import { startLoadingCatalogRoles } from "../../app/actions/catalogRoleActions";
import { startLoadingCatalogCiiusByLevel1 } from "../../app/actions/catalogCiiuActions";
import { startLoadingCatalogsAll } from "../../app/actions/catalogActions";
import { listFlowRoleUsers } from "../../app/actions/userSearchActions";
import { listMyFlowCoverageGroups } from "../../app/actions/flowCoverageActions";
import NavbarIconsModuleContext from "../generic-nav-bar/NavbarIconsModuleContext";
import Pagination from "../generic-ui/Pagination";
import Select from "../generic-ui/Select";
import { openFlowModuleFicha } from "../../app/helpers/flowCmiOpenRecordHelper";
import { FLOW_MODULE_DEFINITIONS, canInboxReassign, canPoolOperate, canPoolView, getModuleDisplayLabel } from "../../app/helpers/flowEngineConfigHelper";
import { ALL_PHASE_FILTER_OPTIONS, getCasePhaseMeta, resolveCasePhase } from "../../app/helpers/flowCasePhaseHelper";
import { FLOW_ORIGIN_NAV } from "../../app/helpers/moduleFichaNavHelper";
import { startDeleteModuleRecord } from "../../app/helpers/moduleRecordDeleteHelper";
import { canSoftDeleteFlowRecord, resolveAuthUserId } from "../../app/helpers/flowEditPageHelper";
import { canOperateCmiRecord } from "../../app/helpers/flowTraySurfaceHelper";
import {
  getRoleDisplayLabel,
  sortRolesByDisplayOrder,
  canonicalizeSpecialistRoleName
} from "../../app/helpers/specialistRoleModuleHelper";
import { RolesEnum } from "../../app/types/RolesEnum";
import {
  CMI_SPECIALIST_USER_ROLE_KEYS,
  FLOW_SPECIALIST_ROLE_NAMES,
  flowCaseKey,
  formatFilteredPair,
  formatSpecialistUserLabel,
  summarizeCaseMassSelection
} from "../../app/helpers/flowCaseMassHelper";
import useActiveSpecialistRole from "../../app/hooks/useActiveSpecialistRole";
import { loadTrayBoardState, saveTrayBoardState } from "../../app/helpers/flowTrayFilterPersistHelper";

const PAGE_SIZE_OPTIONS = [2, 3, 4, 5, 10, 15, 20, 25, 50, 100];
const EMPTY_MODULE_ACCESS = { allowed: [], orphan: [], visible: [] };
const EMPTY_CMI_FILTERS = {
  process: "",
  text: "",
  state_catalog_id: "",
  current_role_catalog_id: "",
  current_user_id: "",
  catalogciius_id: "",
  catalogserie_id: "",
  flow_coverage_group_id: "",
  from: "",
  to: "",
  case_phase: "",
  published_status: "",
  visibility_status: "",
  assignment_status: ""
};

const CIRCUIT_TRAY_ROLE_NAMES = new Set([
  RolesEnum.SPECIALIST_PRE_MANAGER,
  RolesEnum.SPECIALIST_MANAGER,
  RolesEnum.SPECIALIST_VALIDATOR,
  RolesEnum.SPECIALIST_VERIFICATOR,
  RolesEnum.SPECIALIST_EDITOR
]);

const CIRCUIT_USER_ROLE_KEYS = ["pre-manager", "manager", "validator", "verificator", "editor"];
const CMI_PUBLISHED_STATUSES = new Set(["published", "unpublished", "eliminated"]);
const CMI_VISIBILITY_STATUSES = new Set(["visible", "hidden"]);

const circuitUserRoleKeysForTray = (roleName = "") => {
  const canonical = canonicalizeSpecialistRoleName(roleName);
  if (canonical === RolesEnum.SPECIALIST_PRE_MANAGER) return ["pre-manager"];
  if (canonical === RolesEnum.SPECIALIST_MANAGER) return ["manager"];
  if (canonical === RolesEnum.SPECIALIST_VALIDATOR) return ["validator", "verificator"];
  if (canonical === RolesEnum.SPECIALIST_EDITOR) return ["editor"];
  return CIRCUIT_USER_ROLE_KEYS;
};

const normalizeCmiFilters = (filters = {}) => {
  const next = { ...filters };
  if (next.published_status && !CMI_PUBLISHED_STATUSES.has(next.published_status)) {
    next.published_status = "";
  }
  if (next.published_status === "published") {
    if (!CMI_VISIBILITY_STATUSES.has(next.visibility_status)) {
      next.visibility_status = "";
    }
  } else {
    next.visibility_status = "";
  }
  if (next.published_status !== "unpublished") {
    next.assignment_status = "";
    next.current_role_catalog_id = "";
  }
  return next;
};

const MODULES = [
  { key: "", label: "Todos" },
  { key: "technology", label: "Tecnologías-base científica" },
  { key: "alternative", label: "Tecnología Alternativa" },
  { key: "experience", label: "Experiencia de Adaptación" },
  { key: "innovation", label: "Innovación" },
  { key: "assistance", label: "Asistencia Técnica" },
  { key: "financial", label: "Asistencia Financiera" },
  { key: "vision", label: "Visión Analítica" },
  { key: "regulation", label: "Regulación" }
];

const formatDuration = (ms) => {
  if (!ms && ms !== 0) return "—";
  const totalMin = Math.round(Number(ms) / 60000);
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  if (hours <= 0) return `${minutes} min`;
  return `${hours} h ${minutes} min`;
};

const SpecialistFlowCmiScreen = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dashboard, cases, total, page, size, loading, detail, aggregates } = useSelector((state) => state.flowCmi);
  const flowEngine = useSelector((state) => state.flowEngine);
  const { states = [] } = flowEngine;
  const catalogRoles = useSelector((state) => state.catalogRole?.catalogRoles || []);
  const { listCatalogCiiusLevel1 = [] } = useSelector((state) => state.catalogCiiu);
  const { listSeries = [] } = useSelector((state) => state.catalog);
  const configuration = useSelector((state) => state.configuration);
  const authUser = useSelector((state) => state.auth?.user);
  const authUserId = resolveAuthUserId(authUser);
  const actionLoading = useSelector((state) => state.specialistSelection?.actionLoading || {});
  const { canDelete, selectedRole, roleKey, flowEngineEnabled } = useActiveSpecialistRole();
  const cmiOrigin = FLOW_ORIGIN_NAV.cmi;

  const [filters, setFilters] = useState({ ...EMPTY_CMI_FILTERS });
  const [cmiReady, setCmiReady] = useState(false);
  const skipSaveRef = useRef(true);
  const [universeFacets, setUniverseFacets] = useState(null);
  const [moduleAccess, setModuleAccess] = useState(EMPTY_MODULE_ACCESS);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [historyRecord, setHistoryRecord] = useState(null);
  const [popup, setPopup] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [users, setUsers] = useState([]);
  const [coverageGroups, setCoverageGroups] = useState([]);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const createModule = filters.process || "technology";

  const specialistRoles = useMemo(
    () => catalogRoles.filter((role) => FLOW_SPECIALIST_ROLE_NAMES.includes(role.name)),
    [catalogRoles]
  );

  const circuitTrayRoles = useMemo(() => {
    const rows = (catalogRoles || []).filter((role) => CIRCUIT_TRAY_ROLE_NAMES.has(role.name));
    const hasVerificator = rows.some((role) => role.name === RolesEnum.SPECIALIST_VERIFICATOR);
    const filtered = hasVerificator
      ? rows.filter((role) => role.name !== RolesEnum.SPECIALIST_VALIDATOR)
      : rows;
    return sortRolesByDisplayOrder(filtered);
  }, [catalogRoles]);

  const userRoleKeys = useMemo(() => {
    if (!filters.current_role_catalog_id) {
      return CMI_SPECIALIST_USER_ROLE_KEYS;
    }
    const found = specialistRoles.find((role) => Number(role.id) === Number(filters.current_role_catalog_id));
    if (found) {
      return circuitUserRoleKeysForTray(found.name);
    }
    return CMI_SPECIALIST_USER_ROLE_KEYS;
  }, [filters.current_role_catalog_id, specialistRoles]);

  useEffect(() => {
    dispatch(startLoadingCatalogsAll());
    dispatch(startLoadingFlowEngineCatalogs());
    dispatch(startLoadingCatalogRoles());
    dispatch(startLoadingCatalogCiiusByLevel1(1));
    dispatch(startLoadingFlowCmiDashboard());
  }, [dispatch]);

  useEffect(() => {
    const saved = loadTrayBoardState("cmi");
    if (saved?.filters) {
      setFilters(normalizeCmiFilters({ ...EMPTY_CMI_FILTERS, ...saved.filters }));
    }
    if (saved?.itemsPerPage) {
      setItemsPerPage(saved.itemsPerPage);
    }
    if (typeof saved?.advancedOpen === "boolean") {
      setAdvancedOpen(saved.advancedOpen);
    }
    skipSaveRef.current = true;
    setCmiReady(true);
  }, []);

  useEffect(() => {
    if (!cmiReady) {
      return;
    }
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }
    saveTrayBoardState("cmi", { filters, itemsPerPage, advancedOpen });
  }, [advancedOpen, cmiReady, filters, itemsPerPage]);

  const canSeeEliminatedTab = roleKey === "coordinator";
  const canViewPanel = canPoolView(configuration, "panel", roleKey);
  const cmiPoolParams = useMemo(() => (
    canViewPanel
      ? {
        pool_surface: "panel",
        active_catalogrole_id: selectedRole?.id,
        active_role_key: roleKey
      }
      : {}
  ), [canViewPanel, roleKey, selectedRole?.id]);

  const moduleSelectOptions = useMemo(() => {
    const visible = new Set(moduleAccess.visible || []);
    const allowed = new Set(moduleAccess.allowed || []);
    const orphan = new Set(moduleAccess.orphan || []);
    const rows = visible.size
      ? MODULES.filter((item) => !item.key || visible.has(item.key))
      : MODULES;
    return rows.map((item) => ({
      ...item,
      label: item.key && orphan.has(item.key) && !allowed.has(item.key)
        ? `${item.label} ${t("flow.cmi.module-orphan-suffix", "(solo lectura)")}`
        : item.label
    }));
  }, [moduleAccess, t]);

  useEffect(() => {
    if (!cmiReady) {
      return;
    }
    const searchFilters = { ...filters };
    if (!canSeeEliminatedTab && searchFilters.published_status === "eliminated") {
      searchFilters.published_status = "";
      setFilters((prev) => ({ ...prev, published_status: "" }));
    }
    dispatch(startSearchingFlowCmiCases({
      ...searchFilters,
      ...cmiPoolParams,
      page: 0,
      size: itemsPerPage
    }));
    if (canViewPanel) {
      fetchFlowCmiCasesRequest({
        ...cmiPoolParams,
        page: 0,
        size: 1
      }).then((body) => {
        if (body?.ok) {
          setUniverseFacets(body.facets || null);
          setModuleAccess(body.module_access || EMPTY_MODULE_ACCESS);
        }
      });
    } else {
      setUniverseFacets(null);
      setModuleAccess(EMPTY_MODULE_ACCESS);
    }
  }, [dispatch, roleKey, selectedRole?.id, authUserId, itemsPerPage, canViewPanel, cmiPoolParams, cmiReady]);

  useEffect(() => {
    if (!roleKey) {
      setCoverageGroups([]);
      return undefined;
    }
    const moduleId = FLOW_MODULE_DEFINITIONS[filters.process]?.moduleId;
    let cancelled = false;
    const params = { role: roleKey };
    if (moduleId) {
      params.catalogmodule_id = moduleId;
    }
    listMyFlowCoverageGroups(params).then((rows) => {
      if (!cancelled) {
        setCoverageGroups(rows);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [filters.process, roleKey]);

  useEffect(() => {
    let cancelled = false;
    const moduleId = FLOW_MODULE_DEFINITIONS[filters.process]?.moduleId;
    listFlowRoleUsers(userRoleKeys, moduleId).then((rows) => {
      if (!cancelled) {
        setUsers(rows);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [userRoleKeys, filters.process]);

  useEffect(() => {
    if (!filters.current_user_id || !users.length) {
      return;
    }
    if (!users.some((user) => Number(user.id) === Number(filters.current_user_id))) {
      setFilters((prev) => ({ ...prev, current_user_id: "" }));
    }
  }, [filters.current_user_id, users]);

  const hasFilters = Boolean(
    filters.process
    || filters.text
    || filters.state_catalog_id
    || filters.current_role_catalog_id
    || filters.current_user_id
    || filters.catalogciius_id
    || filters.catalogserie_id
    || filters.flow_coverage_group_id
    || filters.from
    || filters.to
    || filters.case_phase
    || filters.published_status
    || filters.visibility_status
    || filters.assignment_status
  );

  const sanitizeCmiSearchFilters = (activeFilters = {}) => {
    const next = { ...activeFilters };
    if (!canSeeEliminatedTab && next.published_status === "eliminated") {
      next.published_status = "";
    }
    return next;
  };

  const runSearch = (pageArg = 0, sizeArg = itemsPerPage, filtersOverride = null) => {
    const activeFilters = sanitizeCmiSearchFilters(filtersOverride || filters);
    dispatch(clearFlowCmiDetail());
    setSelectedRecords([]);
    dispatch(startSearchingFlowCmiCases({
      ...activeFilters,
      ...cmiPoolParams,
      page: pageArg,
      size: sizeArg
    }));
  };

  const handleSearch = (event) => {
    event.preventDefault();
    runSearch(0, itemsPerPage);
  };

  const handleClearFilters = () => {
    const next = normalizeCmiFilters({ ...EMPTY_CMI_FILTERS });
    setFilters(next);
    runSearch(0, itemsPerPage, next);
  };

  const applyFiltersAndSearch = (nextFilters) => {
    const next = normalizeCmiFilters(nextFilters);
    setFilters(next);
    runSearch(0, itemsPerPage, next);
  };

  const applyPublishedSlice = (publishedStatus) => {
    if (publishedStatus === "eliminated" && !canSeeEliminatedTab) {
      return;
    }
    applyFiltersAndSearch({
      ...filters,
      published_status: publishedStatus === filters.published_status ? "" : publishedStatus
    });
  };

  const applyVisibilitySlice = (visibilityStatus) => {
    applyFiltersAndSearch({
      ...filters,
      visibility_status: visibilityStatus === filters.visibility_status ? "" : visibilityStatus
    });
  };

  const applyTrayRoleFilter = (trayRoleId) => {
    applyFiltersAndSearch({
      ...filters,
      current_role_catalog_id: trayRoleId,
      current_user_id: filters.assignment_status === "unassigned" ? "" : filters.current_user_id
    });
  };

  const applyAssignmentStatusFilter = (status) => {
    applyFiltersAndSearch({
      ...filters,
      assignment_status: status,
      current_user_id: status === "unassigned" ? "" : filters.current_user_id
    });
  };

  const handleFilterBarChange = (nextFilters) => {
    if (nextFilters.assignment_status === "unassigned" && nextFilters.current_user_id) {
      setFilters(normalizeCmiFilters({ ...nextFilters, assignment_status: "" }));
      return;
    }
    setFilters(normalizeCmiFilters(nextFilters));
  };

  const canOperateRecord = (row) => canOperateCmiRecord({
    record: row,
    roleKey,
    roleId: selectedRole?.id,
    userId: authUserId
  });

  const handleClearField = (field) => {
    const next = { ...filters, [field]: "" };
    setFilters(next);
    runSearch(0, itemsPerPage, next);
  };

  const onPageChange = (pageClick) => {
    runSearch(pageClick - 1, itemsPerPage);
  };

  const onChangeSelect = (event) => {
    const nextSize = Number(event.target.value) || 10;
    setItemsPerPage(nextSize);
    runSearch(0, nextSize);
  };

  const stateLabel = (id) => states.find((item) => Number(item.id) === Number(id))?.name || id || "—";

  const trayLabel = (row) => {
    if (row?.published_at || !row?.current_role_catalog_id) {
      return t("flow.cmi.out-of-tray", "Fuera de bandeja");
    }
    const found = catalogRoles.find((role) => Number(role.id) === Number(row.current_role_catalog_id));
    return found ? getRoleDisplayLabel(found.name, t) : row.current_role_catalog_id;
  };

  const userLabel = (row) => {
    if (!row?.current_user_id) {
      if (row?.published_at || !row?.current_role_catalog_id) {
        return "—";
      }
      return t("flow.pool.filter.pool-badge", "Pool");
    }
    const found = users.find((user) => Number(user.id) === Number(row.current_user_id));
    return found ? formatSpecialistUserLabel(found) : `#${row.current_user_id}`;
  };

  const openCase = async (row) => {
    const body = await dispatch(startLoadingFlowCmiCase(row.catalogmodule_id, row.id));
    const record = body?.record || row;
    openFlowModuleFicha(row.process, record, dispatch, navigate, { origin: "cmi" });
  };

  const handleDeleteCase = (row) => {
    dispatch(startDeleteModuleRecord(row.process, row.id, () => runSearch(page, itemsPerPage)));
  };

  const selectedKeys = useMemo(() => new Set(selectedRecords.map(flowCaseKey)), [selectedRecords]);
  const operablePageRows = (cases || []).filter(canOperateRecord);
  const allPageSelected = operablePageRows.length > 0
    && operablePageRows.every((row) => selectedKeys.has(flowCaseKey(row)));
  const massSummary = useMemo(
    () => summarizeCaseMassSelection(selectedRecords, { roleKey }),
    [roleKey, selectedRecords]
  );

  const selectedTrayRole = useMemo(() => {
    if (!massSummary.roleId) {
      return null;
    }
    return specialistRoles.find((role) => Number(role.id) === Number(massSummary.roleId)) || null;
  }, [massSummary.roleId, specialistRoles]);

  const reassignUsers = useMemo(() => {
    if (!selectedTrayRole) {
      return users;
    }
    return users.filter((user) => (
      (user.roles || []).includes(selectedTrayRole.name)
      || (user.catalogrole_ids || []).some((id) => Number(id) === Number(selectedTrayRole.id))
    ));
  }, [selectedTrayRole, users]);

  const canReassign = canInboxReassign(configuration, roleKey);

  const toggleRow = (row) => {
    if (!canOperateRecord(row)) {
      return;
    }
    const key = flowCaseKey(row);
    setSelectedRecords((prev) => (
      prev.some((item) => flowCaseKey(item) === key)
        ? prev.filter((item) => flowCaseKey(item) !== key)
        : [...prev, row]
    ));
  };

  const togglePage = () => {
    const pageRows = (cases || []).filter(canOperateRecord);
    if (allPageSelected) {
      const pageKeys = new Set(pageRows.map(flowCaseKey));
      setSelectedRecords((prev) => prev.filter((item) => !pageKeys.has(flowCaseKey(item))));
      return;
    }
    setSelectedRecords((prev) => {
      const next = [...prev];
      const keys = new Set(prev.map(flowCaseKey));
      pageRows.forEach((row) => {
        if (!keys.has(flowCaseKey(row))) {
          next.push(row);
        }
      });
      return next;
    });
  };

  const cards = useMemo(() => ([
    {
      label: t("flow.cmi.total", "Total de casos"),
      value: formatFilteredPair(aggregates?.total ?? total, dashboard?.total || 0, hasFilters)
    },
    {
      label: t("flow.cmi.modules", "Módulos"),
      value: formatFilteredPair(
        aggregates?.modulesWithResults || 0,
        dashboard?.byModule?.length || 0,
        hasFilters
      )
    },
    {
      label: t("flow.cmi.trays", "Bandejas con carga"),
      value: formatFilteredPair(
        aggregates?.traysWithLoad || 0,
        dashboard?.traysWithLoad || 0,
        hasFilters
      )
    },
    {
      label: t("flow.cmi.published", "Publicados"),
      value: formatFilteredPair(
        aggregates?.published || 0,
        dashboard?.published || 0,
        hasFilters
      )
    }
  ]), [aggregates, dashboard, hasFilters, t, total]);

  const historyModuleId = historyRecord
    ? FLOW_MODULE_DEFINITIONS[historyRecord.process]?.moduleId
    : null;

  return (
    <FlowEngineGlobalGate>
      <NavbarIconsModuleContext
        moduleProcess={createModule}
        mode="workspace"
        returnTo={cmiOrigin.returnTo}
        returnLabelKey={cmiOrigin.returnLabelKey}
        returnLabel={cmiOrigin.returnLabel}
      />
      <div className="container-fluid py-3">
        <h3 className="mb-1">{t("flow.cmi.title", "Panel de control de casos")}</h3>
        <p className="small text-muted mb-3">
          {t(
            "flow.cmi.hint-versatile",
            "Bandeja general: consulta todos los casos. Cada perfil opera solo su competencia (dueño o pool de su bandeja). El Coordinador opera en todas las fases; el Enlace solo publicados."
          )}
        </p>
        <div className="row g-3 mb-4">
          {cards.map((card) => (
            <div className="col-md-3" key={card.label}>
              <div className="card">
                <div className="card-body">
                  <div className="text-muted small">{card.label}</div>
                  <div className="fs-3 fw-semibold">{card.value}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <FlowCaseFilterBar
          filters={filters}
          onChange={handleFilterBarChange}
          onSearch={handleSearch}
          loading={loading}
          modules={moduleSelectOptions}
          states={states}
          catalogRoles={specialistRoles}
          users={users}
          ciius={(listCatalogCiiusLevel1 || []).filter((item) => !item.parent_id)}
          series={listSeries}
          showTrayFilter={filters.published_status === "unpublished"}
          showUserFilter
          userFirstRow
          showPhaseFilter
          phaseOptions={ALL_PHASE_FILTER_OPTIONS}
          phaseInAdvanced
          showClearFilters
          showFilterMarkers
          availableFacets={universeFacets}
          onClearFilters={handleClearFilters}
          onClearField={handleClearField}
          advancedOpen={advancedOpen}
          onToggleAdvanced={() => setAdvancedOpen((open) => !open)}
          coverageGroups={coverageGroups}
        />
        <div className="mb-3">
          <div className="d-flex flex-wrap gap-2">
            <div className="btn-group btn-group-sm" role="group" aria-label={t("flow.pool.filter.published-group", "Publicación")}>
              <button type="button" className={`btn ${filters.published_status === "published" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => applyPublishedSlice("published")}>
                {t("flow.pool.filter.published", "Publicados")}
              </button>
              <button type="button" className={`btn ${filters.published_status === "unpublished" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => applyPublishedSlice("unpublished")}>
                {t("flow.pool.filter.unpublished", "No publicados")}
              </button>
              {canSeeEliminatedTab && (
                <button type="button" className={`btn ${filters.published_status === "eliminated" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => applyPublishedSlice("eliminated")}>
                  {t("flow.pool.filter.eliminated", "Eliminados")}
                </button>
              )}
            </div>
            {filters.published_status === "published" && (
              <div className="btn-group btn-group-sm" role="group" aria-label={t("flow.pool.filter.visibility-group", "Visibilidad")}>
                <button type="button" className={`btn ${filters.visibility_status === "visible" ? "btn-secondary" : "btn-outline-secondary"}`} onClick={() => applyVisibilitySlice("visible")}>
                  {t("flow.pool.filter.visible", "Visibles")}
                </button>
                <button type="button" className={`btn ${filters.visibility_status === "hidden" ? "btn-secondary" : "btn-outline-secondary"}`} onClick={() => applyVisibilitySlice("hidden")}>
                  {t("flow.pool.filter.hidden", "No visibles")}
                </button>
              </div>
            )}
          </div>
          <p className="small text-muted mb-0 mt-2">
            {filters.published_status === "unpublished"
              ? t("flow.pool.filter.hint-unpublished", "Casos en flujo, en cualquier bandeja o pool, asignados o sin asignar. No incluye dados de baja.")
              : filters.published_status === "eliminated"
                ? t("flow.pool.filter.hint-eliminated", "Dados de baja. Ya no están en el catálogo ni en el flujo.")
                : filters.published_status === "published"
                  ? t("flow.pool.filter.hint-published", "Ya publicados. Visibles llegan al usuario final; no visibles están pausados.")
                  : canSeeEliminatedTab
                    ? t("flow.cmi.filter.hint-all", "Sin corte de publicación: incluye en flujo y publicados. Use Eliminados para ver dados de baja.")
                    : t("flow.cmi.filter.hint-all-roles", "Sin corte de publicación: incluye casos en flujo y publicados.")}
          </p>
          {filters.published_status === "unpublished" && (
          <div className="row g-3 mt-1">
            <div className="col-md-3">
              <label className="form-label mb-1" htmlFor="cmi-tray-role">
                {t("flow.pool.filter.tray-role", "Bandeja (rol)")}
              </label>
              <select
                id="cmi-tray-role"
                className={`form-select form-select-sm${filters.current_role_catalog_id ? " border-danger" : ""}`}
                value={filters.current_role_catalog_id || ""}
                onChange={(event) => applyTrayRoleFilter(event.target.value)}
              >
                <option value="">{t("flow.cmi.all", "Todas")}</option>
                {circuitTrayRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {getRoleDisplayLabel(role.name, t)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label mb-1" htmlFor="cmi-assignment-status">
                {t("flow.pool.filter.assignment", "Asignación")}
              </label>
              <select
                id="cmi-assignment-status"
                className={`form-select form-select-sm${filters.assignment_status ? " border-danger" : ""}`}
                value={filters.assignment_status || ""}
                onChange={(event) => applyAssignmentStatusFilter(event.target.value)}
              >
                <option value="">{t("flow.pool.filter.assignment-all", "Todos")}</option>
                <option value="assigned">{t("flow.pool.filter.assignment-assigned", "Asignados")}</option>
                <option value="unassigned">{t("flow.pool.filter.assignment-unassigned", "Sin asignar (pool)")}</option>
              </select>
            </div>
            <div className="col-12">
              <p className="small text-muted mb-0">
                {t(
                  "flow.pool.filter.hint-unpublished-tray",
                  "Filtra por bandeja del circuito (Pre-Gestor, Gestor, Verificador, Editor) y por si el caso está asignado o en el pool de autoasignación."
                )}
              </p>
            </div>
          </div>
          )}
        </div>
        {filters.process && (moduleAccess.orphan || []).includes(filters.process)
          && !(moduleAccess.allowed || []).includes(filters.process) && (
          <div className="alert alert-warning py-2 small">
            {t(
              "flow.cmi.module-readonly",
              "No tiene permiso para operar en este módulo. Puede consultar los casos asignados a usted, en solo lectura."
            )}
          </div>
        )}

        <FlowCaseMassToolbar
          records={cases || []}
          selectedRecords={selectedRecords}
          onSelectionChange={setSelectedRecords}
          onAfterAction={() => runSearch(page, itemsPerPage)}
          poolSurface="panel"
          roleId={selectedRole?.id}
          roleKey={roleKey}
          canSelectRecord={canOperateRecord}
          showFlow={
            canPoolOperate(configuration, "panel", roleKey)
            && selectedRecords.length > 0
            && selectedRecords.every(canOperateRecord)
          }
        />
        <FlowMassReassignPanel
          records={selectedRecords}
          users={reassignUsers}
          roleKey={selectedTrayRole?.name || ""}
          activeRoleKey={roleKey}
          canReassign={canReassign}
          loading={Boolean(actionLoading.assign)}
          currentUserId={authUserId}
          onAssigned={() => runSearch(page, itemsPerPage)}
        />

        <div className="table-responsive mb-4">
          <table className="table table-sm table-hover">
            <thead>
              <tr>
                <th style={{ width: "40px" }}>
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={togglePage}
                    disabled={!operablePageRows.length}
                  />
                </th>
                <th>{t("flow.cmi.col.module", "Módulo")}</th>
                <th>{t("flow.cmi.col.name", "Nombre")}</th>
                <th>{t("flow.cmi.col.state", "Estado")}</th>
                <th>{t("flow.cmi.col.phase", "Fase")}</th>
                <th>{t("flow.cmi.col.tray", "Bandeja")}</th>
                <th>{t("flow.cmi.user", "Usuario")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(cases || []).map((row) => (
                <tr key={flowCaseKey(row)}>
                  <td>
                    {canOperateRecord(row) ? (
                      <input
                        type="checkbox"
                        checked={selectedKeys.has(flowCaseKey(row))}
                        onChange={() => toggleRow(row)}
                      />
                    ) : null}
                  </td>
                  <td>{getModuleDisplayLabel(row.process, t)}</td>
                  <td>
                    {row.process === "vision" ? (
                      <TrayVisionName text={row.name} onOpen={() => openCase(row)} />
                    ) : (
                      row.name
                    )}
                  </td>
                  <td>
                    <FlowStateSemaphore stateCatalogId={row.state_catalog_id} interactive={false} />
                    <span className="ms-2 small">{stateLabel(row.state_catalog_id)}</span>
                    {row.published_at && row.is_visible === false && (
                      <span className="badge bg-secondary ms-2">
                        {t("general.flow-engine.paused-badge", "No visible")}
                      </span>
                    )}
                  </td>
                  <td>
                    {(() => {
                      const phase = row.case_phase || resolveCasePhase(row, { process: row.process });
                      const meta = getCasePhaseMeta(phase);
                      return (
                        <span className={`badge bg-${meta.tone}`}>
                          {t(meta.key, meta.fallback)}
                        </span>
                      );
                    })()}
                  </td>
                  <td>{trayLabel(row)}</td>
                  <td>{userLabel(row)}</td>
                  <td className="text-nowrap">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary me-2"
                      title={t("flow.cmi.history", "Historial de navegación")}
                      onClick={() => setHistoryRecord(row)}
                    >
                      <i className="fa fa-history" />
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openCase(row)}>
                      {t("flow.cmi.view", "Ver")}
                    </button>
                    {canOperateRecord(row) && (
                    <RecordExcelUpdateRowButton
                      record={row}
                      moduleProcess={row.process}
                      onApplied={() => runSearch(page, itemsPerPage)}
                      tooltipScope={`cmi-${row.process}-${row.id}`}
                      className="ms-2"
                    />
                    )}
                    {canOperateRecord(row) && canSoftDeleteFlowRecord({
                      record: row,
                      flowEngineEnabled,
                      canDelete,
                      selectedRole,
                      flowEngine
                    }) && (
                      <button
                        type="button"
                        className="table-button-delete ms-2"
                        title={t("buttons.delete", "Eliminar")}
                        onClick={() => handleDeleteCase(row)}
                      >
                        <i className="fa fa-trash" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
            <div className="small text-muted">{t("flow.cmi.results", "{{count}} resultados", { count: total })}</div>
            <div className="d-flex align-items-center gap-2">
              <span className="small">{t("flow.cmi.per-page", "Registros por página")}</span>
              <Select
                listOptions={PAGE_SIZE_OPTIONS}
                onChangeSelect={onChangeSelect}
                pageSize={itemsPerPage}
              />
            </div>
          </div>
          <div className="pagination justify-content-center mt-2">
            <Pagination
              totalPages={Math.ceil((total || 0) / (size || itemsPerPage || 10))}
              totalItems={total}
              currentPage={(page || 0) + 1}
              itemsPerPage={size || itemsPerPage}
              onPageChange={onPageChange}
            />
          </div>
        </div>

        {detail?.record && String(configuration.flowCmiTimelineEnabled || 'false').toLowerCase() === 'true' && (
          <div className="card">
            <div className="card-header d-flex justify-content-between">
              <strong>{detail.record.name}</strong>
              <span className="small text-muted">
                {t("flow.cmi.total-time", "Tiempo total")}: {formatDuration(detail.timeline?.totals?.total_ms)}
                {" · "}
                {t("flow.cmi.wait-time", "Esperas")}: {formatDuration(detail.timeline?.totals?.wait_ms)}
              </span>
            </div>
            <div className="card-body">
              <ol className="mb-0">
                {(detail.timeline?.items || []).map((event) => (
                  <li key={event.id} className="mb-2">
                    <button type="button" className="btn btn-link p-0" onClick={() => setPopup(event.popup)}>
                      {event.rule?.description || event.rule?.value || event.popup?.action || t("flow.cmi.step", "Paso del caso")}
                    </button>
                    <span className="small text-muted ms-2">{formatDuration(event.duration_ms)}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}

        {popup && (
          <div className="modal d-block" style={{ background: "rgba(0,0,0,.35)" }} onClick={() => setPopup(null)}>
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t("flow.cmi.popup-title", "Detalle de la transacción")}</h5>
                  <button type="button" className="btn-close" onClick={() => setPopup(null)} />
                </div>
                <div className="modal-body">
                  <p><strong>{t("flow.cmi.action", "Acción")}:</strong> {popup.action || "—"}</p>
                  <p><strong>{t("flow.cmi.comment", "Comentario")}:</strong> {popup.comment || t("flow.cmi.no-comment", "Sin observación")}</p>
                  <p><strong>{t("flow.cmi.duration", "Duración")}:</strong> {formatDuration(popup.duration_ms)}</p>
                  <p className="mb-0"><strong>{t("flow.cmi.when", "Fecha")}:</strong> {popup.created_at}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {historyRecord && historyModuleId && (
          <FlowRecordHistoryModal
            show
            onHide={() => setHistoryRecord(null)}
            record={historyRecord}
            moduleId={historyModuleId}
            moduleProcess={historyRecord.process}
          />
        )}
      </div>
    </FlowEngineGlobalGate>
  );
};

export default SpecialistFlowCmiScreen;
